/** Configurator: report sets → click a gauge → assign KPI, scale, thresholds → live preview. */
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'wouter';
import {
  assignmentSettingsSchema, defaultOrgSettings, evaluateSet, findGauge,
  type AssignmentSettings, type ClusterModel, type OrgSettings, type ReportSetInput, type LatestValue, type NeedleSettings,
} from '@dg/core';
import { api, ApiError, type Me } from '../api.ts';
import { track } from '../analytics.ts';
import { ClusterPreview } from '../components/ClusterPreview.tsx';
import { GaugeEditor, defaultSettings, type Metric } from '../components/GaugeEditor.tsx';
import { DeviceAdmin, DeviceHealth, type Health } from '../components/DeviceHealth.tsx';
import { HistoryChart } from '../components/HistoryChart.tsx';

interface Assignment { gaugeId: string; metricId: string | null; settings: AssignmentSettings }
interface ReportSet { id: string; name: string; position: number; colorMode: 'status' | 'fixed'; fixedColor: string | null; assignments: Assignment[] }
interface Device { id: string; name: string; serial: string; model: ClusterModel; reportSets: ReportSet[] }
interface Live { now: number; values: Record<string, LatestValue | undefined>; health?: Health }

export function Configurator({ id, me }: { id: string; me: Me }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const device = useQuery({ queryKey: ['device', id], queryFn: () => api<Device>(`/api/devices/${id}`) });
  const metrics = useQuery({ queryKey: ['metrics'], queryFn: () => api<(Metric & { staleAfterMinutes: number | null })[]>('/api/metrics') });
  const org = useQuery({ queryKey: ['org-settings'], queryFn: () => api<OrgSettings>('/api/org/settings') });
  const live = useQuery({ queryKey: ['live', id], queryFn: () => api<Live>(`/api/devices/${id}/live`), refetchInterval: 3000 });

  const [setIndex, setSetIndex] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, Assignment>>({});
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const readOnly = me.role === 'viewer';
  const health = live.data?.health;

  const d = device.data;
  const set = d?.reportSets[setIndex] ?? d?.reportSets[0];
  const draftKey = (gaugeId: string) => `${set?.id}:${gaugeId}`;

  const assignmentsFor = (s: ReportSet): Assignment[] => {
    const merged = new Map(s.assignments.map((a) => [a.gaugeId, a]));
    for (const [k, a] of Object.entries(drafts)) if (k.startsWith(`${s.id}:`)) merged.set(a.gaugeId, a);
    return [...merged.values()].filter((a) => a.metricId || a.settings);
  };

  const view = useMemo(() => {
    if (!d || !set) return null;
    const input: ReportSetInput = {
      position: set.position,
      name: set.name,
      colorMode: set.colorMode,
      fixedColor: set.fixedColor,
      // Unsaved edits are previewed only when they are valid.
      assignments: assignmentsFor(set).flatMap((a) => {
        const parsed = assignmentSettingsSchema.safeParse(a.settings);
        return parsed.success ? [{ gaugeId: a.gaugeId, metricId: a.metricId, settings: parsed.data }] : [];
      }),
    };
    return evaluateSet(input, {
      model: d.model,
      org: org.data ?? defaultOrgSettings(),
      metrics: Object.fromEntries((metrics.data ?? []).map((m) => [m.id, { staleAfterMinutes: m.staleAfterMinutes }])),
      values: live.data?.values ?? {},
      now: Date.now(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d, set, drafts, org.data, metrics.data, live.data]);

  if (device.isError) return <p className="error">{t('common.error')}</p>;
  if (!d || !set || !view) return <p className="muted">{t('common.loading')}</p>;

  const gauge = selected ? findGauge(d.model, selected) : undefined;
  const current = gauge ? drafts[draftKey(gauge.id)] ?? set.assignments.find((a) => a.gaugeId === gauge.id) ?? { gaugeId: gauge.id, metricId: null, settings: defaultSettings(gauge) } : undefined;

  const settingsFor = (gaugeId: string): NeedleSettings | undefined => {
    const a = assignmentsFor(set).find((x) => x.gaugeId === gaugeId);
    if (!a?.metricId) return undefined;
    const p = assignmentSettingsSchema.safeParse(a.settings);
    return p.success && p.data.kind === 'needle' ? p.data : undefined;
  };

  const switchSet = (i: number) => {
    setSetIndex(i);
    setSelected(null);
    setError(null);
    track('report_set_switched', { set_name: d.reportSets[i]?.name, demo: !!me.demo });
  };

  async function save() {
    if (!gauge || !current) return;
    const parsed = assignmentSettingsSchema.safeParse(current.settings);
    if (!parsed.success) return setError(t('editor.invalid', { details: parsed.error.issues.map((i) => i.message).join(', ') }));
    setSaving(true);
    setError(null);
    try {
      await api(`/api/report-sets/${set!.id}/assignments/${gauge.id}`, { method: 'PUT', body: { metricId: current.metricId, settings: parsed.data } });
      await qc.invalidateQueries({ queryKey: ['device', id] });
      setDrafts(({ [draftKey(gauge.id)]: _, ...rest }) => rest);
      setSavedAt(Date.now());
      const metric = metrics.data?.find((m) => m.id === current.metricId);
      track('gauge_assigned', { gauge: gauge.id, kpi: metric?.name, demo: !!me.demo });
    } catch (e) {
      setError(e instanceof ApiError ? `${t('common.error')} (${e.code})` : t('common.error'));
    } finally {
      setSaving(false);
    }
  }

  async function clear() {
    if (!gauge) return;
    await api(`/api/report-sets/${set!.id}/assignments/${gauge.id}`, { method: 'DELETE' });
    setDrafts(({ [draftKey(gauge.id)]: _, ...rest }) => rest);
    await qc.invalidateQueries({ queryKey: ['device', id] });
  }

  async function addSet() {
    const used = new Set(d!.reportSets.map((s) => s.position));
    const position = [...Array(16).keys()].find((p) => !used.has(p));
    if (position === undefined) return;
    await api(`/api/devices/${id}/report-sets`, { method: 'POST', body: { name: `${t('configurator.newSetName')} ${position + 1}`, position } });
    await qc.invalidateQueries({ queryKey: ['device', id] });
    switchSet(d!.reportSets.length);
  }

  async function patchSet(body: object) {
    await api(`/api/report-sets/${set!.id}`, { method: 'PATCH', body });
    await qc.invalidateQueries({ queryKey: ['device', id] });
  }

  async function removeSet() {
    await api(`/api/report-sets/${set!.id}`, { method: 'DELETE' });
    setSetIndex(0);
    setSelected(null);
    await qc.invalidateQueries({ queryKey: ['device', id] });
  }

  return (
    <div className="stack configurator">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <div>
          <p className="faint"><Link href="/">{t('nav.devices')}</Link> / {d.model.label}</p>
          <h1>{d.name}</h1>
        </div>
      </div>

      <section aria-labelledby="sets-title" className="stack">
        <h2 id="sets-title" className="visually-hidden">{t('configurator.reportSets')}</h2>
        <div className="tabs" role="tablist" aria-label={t('configurator.reportSets')}>
          {d.reportSets.map((s, i) => (
            <button key={s.id} role="tab" className="tab" aria-selected={s.id === set.id} onClick={() => switchSet(i)}>
              {s.name}
              <small>
                {t('configurator.position', { n: s.position + 1 })}
                {health?.activeSet === s.position && <span className="on-unit"> · {t('configurator.onUnit')}</span>}
              </small>
            </button>
          ))}
          {!readOnly && <button className="tab" onClick={addSet}>+ {t('configurator.addSet')}</button>}
        </div>
        <p className="faint">{t('configurator.reportSetsHint')}</p>
      </section>

      <div className="config-grid">
        <div className="stack">
          <div className="preview-frame">
            <ClusterPreview model={d.model} view={view} settingsFor={settingsFor} colors={(org.data ?? defaultOrgSettings()).colors} selected={selected} onSelect={(g) => (setSelected(g), setError(null), setSavedAt(null))} />
          </div>
          <p className="faint">{t('configurator.clickGauge')}</p>
          {!readOnly && <SetName key={set.id} name={set.name} canRemove={d.reportSets.length > 1} onRename={(name) => patchSet({ name })} onRemove={removeSet} />}
          {!readOnly && (
            <div className="row">
              <div className="field">
                <label htmlFor="cm">{t('configurator.colorMode')}</label>
                <select id="cm" value={set.colorMode} onChange={(e) => patchSet({ colorMode: e.target.value, fixedColor: set.fixedColor ?? '#3D7BFF' })}>
                  <option value="status">{t('configurator.colorModeStatus')}</option>
                  <option value="fixed">{t('configurator.colorModeFixed')}</option>
                </select>
              </div>
              {set.colorMode === 'fixed' && (
                <div className="field">
                  <label htmlFor="fc" className="visually-hidden">{t('configurator.colorModeFixed')}</label>
                  <input id="fc" type="color" value={set.fixedColor ?? '#3D7BFF'} onChange={(e) => patchSet({ fixedColor: e.target.value.toUpperCase() })} />
                </div>
              )}
            </div>
          )}
        </div>

        {gauge && current ? (
          <GaugeEditor
            lights={d.model.gauges.filter((g) => g.type === 'warning_light').map((g) => ({ id: g.id, label: g.label }))}
            key={`${set.id}:${gauge.id}`}
            gauge={gauge}
            metrics={metrics.data ?? []}
            metricId={current.metricId}
            settings={current.settings}
            onChange={(metricId, settings) => setDrafts((all) => ({ ...all, [draftKey(gauge.id)]: { gaugeId: gauge.id, metricId, settings } }))}
            onSave={save}
            onClear={clear}
            saving={saving}
            savedAt={savedAt}
            error={error}
            readOnly={readOnly}
          />
        ) : (
          <div className="card muted">{t('configurator.clickGauge')}</div>
        )}
      </div>

      {current?.metricId && (
        <HistoryChart metricId={current.metricId} name={metrics.data?.find((m) => m.id === current.metricId)?.name ?? ''} />
      )}

      {health && (
        <div className="device-grid">
          <DeviceHealth health={health} setNames={Object.fromEntries(d.reportSets.map((s) => [s.position, s.name]))} />
          {me.role === 'owner' && !me.demo && <DeviceAdmin key={d.name} id={d.id} name={d.name} serial={d.serial} />}
        </div>
      )}
    </div>
  );
}

/** Name of the active report set (saved on Enter / leaving the field) and removing the set. */
function SetName({ name, canRemove, onRename, onRemove }: { name: string; canRemove: boolean; onRename: (name: string) => Promise<void>; onRemove: () => Promise<void> }) {
  const { t } = useTranslation();
  const [value, setValue] = useState(name);
  const [state, setState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [confirming, setConfirming] = useState(false);

  async function commit() {
    const next = value.trim();
    if (!next || next === name) return setValue(name);
    setState('saving');
    try {
      await onRename(next);
      setState('saved');
    } catch {
      setState('error');
    }
  }

  return (
    <div className="row" style={{ alignItems: 'end' }}>
      <div className="field">
        <label htmlFor="set-name">{t('configurator.setName')}</label>
        <input id="set-name" value={value} maxLength={60} onChange={(e) => (setValue(e.target.value), setState('idle'))} onBlur={commit} onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), e.currentTarget.blur())} />
      </div>
      {state === 'saved' && <span className="ok" role="status">{t('editor.saved')}</span>}
      {state === 'error' && <span className="error" role="alert">{t('common.error')}</span>}
      {canRemove && (confirming ? (
        <>
          <button type="button" className="btn btn--small" onClick={() => onRemove().catch(() => setState('error')).finally(() => setConfirming(false))}>{t('configurator.removeSetConfirm')}</button>
          <button type="button" className="btn btn--small btn--ghost" onClick={() => setConfirming(false)}>{t('connections.cancel')}</button>
        </>
      ) : (
        <button type="button" className="btn btn--small btn--ghost" onClick={() => setConfirming(true)}>{t('configurator.removeSet')}</button>
      ))}
    </div>
  );
}
